import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Divider,
  Chip,
  Alert,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import { Refresh, Send, Person, AccessTime } from '@mui/icons-material';

const AdminChatInterface = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [responseMessage, setResponseMessage] = useState('');
  const [adminName, setAdminName] = useState('Angelina'); // Default
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/chatbot/admin/conversations');
      const data = await response.json();
      
      if (response.ok) {
        setConversations(data.conversations);
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to fetch conversations');
    } finally {
      setLoading(false);
    }
  };

  const sendResponse = async () => {
    if (!selectedConversation || !responseMessage.trim()) return;

    try {
      setLoading(true);
      const response = await fetch('/api/chatbot/admin-response', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: selectedConversation.sessionId,
          message: responseMessage.trim(),
          adminName: adminName
        }),
      });

      const data = await response.json();
      
      if (response.ok) {
        setSuccess(`Response sent successfully to ${selectedConversation.userEmail || 'user'}!`);
        setResponseMessage('');
        setSelectedConversation(null);
        fetchConversations(); // Refresh list
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Failed to send response');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    // Auto-refresh every 10 seconds for faster updates
    const interval = setInterval(fetchConversations, 10000);
    
    // Browser notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    
    return () => clearInterval(interval);
  }, []);

  // Show browser notification for new conversations
  useEffect(() => {
    if (conversations.length > 0 && 'Notification' in window && Notification.permission === 'granted') {
      const latestConversation = conversations[0];
      const timeDiff = Date.now() - new Date(latestConversation.updatedAt).getTime();
      
      // If conversation is less than 30 seconds old, show notification
      if (timeDiff < 30000) {
        new Notification('🚨 New Auralyze Escalation!', {
          body: `From: ${latestConversation.userEmail || 'Anonymous'}\nMessage: "${latestConversation.lastMessage?.content?.substring(0, 50)}..."`,
          icon: '/favicon.ico',
          tag: latestConversation.sessionId
        });
      }
    }
  }, [conversations]);

  const formatTime = (date) => {
    return new Date(date).toLocaleString();
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Auralyze Admin Chat
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchConversations}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              if ('Notification' in window) {
                if (Notification.permission === 'granted') {
                  new Notification('🧪 Test Notification', {
                    body: 'This is how escalation notifications will appear!',
                    icon: '/favicon.ico'
                  });
                } else if (Notification.permission === 'default') {
                  Notification.requestPermission().then(permission => {
                    if (permission === 'granted') {
                      new Notification('🧪 Test Notification', {
                        body: 'Notification permissions granted! You\'ll now receive escalation alerts.',
                        icon: '/favicon.ico'
                      });
                    }
                  });
                } else {
                  alert('Notifications are blocked. Enable them in your browser settings for escalation alerts.');
                }
              } else {
                alert('This browser doesn\'t support notifications.');
              }
            }}
          >
            Test Notifications
          </Button>
        </Box>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Conversations List */}
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Escalated Conversations ({conversations.length})
          </Typography>
          
          {conversations.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No escalated conversations at the moment! 🎉
            </Typography>
          ) : (
            <List>
              {conversations.map((conv, index) => (
                <ListItem
                  key={conv.sessionId}
                  sx={{
                    cursor: 'pointer',
                    borderRadius: 1,
                    mb: 1,
                    bgcolor: selectedConversation?.sessionId === conv.sessionId ? 'action.selected' : 'transparent',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                  onClick={() => setSelectedConversation(conv)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Person fontSize="small" color="primary" />
                        <Typography variant="subtitle2">
                          {conv.userEmail || `User #${conv.sessionId.slice(-6)}`}
                        </Typography>
                        <Chip size="small" label={conv.status} color="warning" />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          Last: "{conv.lastMessage?.content?.substring(0, 60)}..."
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          <AccessTime fontSize="small" color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(conv.updatedAt)}
                          </Typography>
                          <Typography variant="caption" color="primary">
                            {conv.messageCount} messages
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        {/* Response Interface */}
        <Paper sx={{ flex: 1, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Send Response
          </Typography>

          {selectedConversation ? (
            <Box>
              <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Conversation with {selectedConversation.userEmail || 'Anonymous User'}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  
                  {/* Show recent messages */}
                  <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                    {selectedConversation.messages.map((msg, idx) => (
                      <Box key={idx} sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">
                          {msg.role === 'user' ? '👤 User' : '🤖 AI'} • {formatTime(msg.timestamp)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {msg.content}
                        </Typography>
                        {idx < selectedConversation.messages.length - 1 && <Divider sx={{ mt: 1 }} />}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>

              {/* Response Form */}
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Your Name</InputLabel>
                  <Select
                    value={adminName}
                    label="Your Name"
                    onChange={(e) => setAdminName(e.target.value)}
                  >
                    <MenuItem value="Angelina">Angelina</MenuItem>
                    <MenuItem value="Faris">Faris</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                multiline
                rows={4}
                value={responseMessage}
                onChange={(e) => setResponseMessage(e.target.value)}
                placeholder="Type your response to the user..."
                variant="outlined"
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={sendResponse}
                disabled={loading || !responseMessage.trim()}
                fullWidth
              >
                Send Response to User
              </Button>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              Select a conversation to respond
            </Typography>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminChatInterface; 